import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Suspense } from "react";
import { env } from "@acme/env/client";
import { Spinner } from "@acme/ui/spinner";
import { ThemeSwitcher } from "@acme/ui/theme-switcher";
import type { SupportedLanguage } from "@acme/i18n";
import { useTheme } from "~/common/hooks/use-theme.js";
import type { AuthContext } from "~/features/auth/_providers/auth.provider.js";
import type { api, queryClient } from "~/common/utils/trpc.js";
import { LanguageSwitcher } from "~/features/i18n/_components/language-switcher.js";
import { useLanguage } from "~/common/hooks/use-language.js";
import { useFirebaseMessaging } from "~/common/lib/firebase/hooks.js";
import { TrpcTestingTrigger } from "~/common/components/trpc-testing-trigger.js";

const RootLayout = () =>
{
  const { theme, setTheme } = useTheme({ defaultTheme: "system" });
  const { locale: _locale, auth } = Route.useRouteContext();
  const isLoggedIn = auth?.isAuthenticated ?? false;

  useFirebaseMessaging({ isLoggedIn });

  // Initialize language from localStorage
  useLanguage();

  if (env.PUBLIC_WEBAPP_IS_IN_MAINTENANCE_MODE)
  {
    return <div>Maintenance Mode</div>; //TODO: add cool animated screen here!
  }

  return (
    <div className="min-h-screen w-screen max-w-screen bg-background">
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
      <TanStackRouterDevtools />
      <div className="fixed bottom-0 right-0 flex flex-col gap-3 p-4">
        <TrpcTestingTrigger />
        <LanguageSwitcher />
        <ThemeSwitcher value={theme} onChange={setTheme} />
      </div>
    </div>
  );
};

function LoadingFallback()
{
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Spinner className="size-8 text-muted-foreground" />
    </div>
  );
}

interface RouterContext
{
  api: typeof api;
  auth: AuthContext | undefined;
  queryClient: typeof queryClient;
  locale: SupportedLanguage;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  beforeLoad: ({ params }) =>
  {
    const locale = (params as any).locale as SupportedLanguage | undefined;
    return {
      locale: locale || "en",
    };
  },
});
